import { useState, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { Download, QrCode as QrCodeIcon, CheckCircle, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import QRCode from "react-qr-code";
import QRCodeCanvas from "qrcode";
import { useAuth } from "@/contexts/AuthContext";
import { PopupDisplay } from "@/components/PopupDisplay";
import { BookingWizard, type BookingFormData } from "@/components/BookingWizard";
import { useActiveServices, useVehicles, useCreateVehicle, useCreateAppointment, useActiveCustomerSubscriptions, useActiveSubscriptionPackages, useSystemSettings, useAppointmentsByDateRange } from "@/hooks/useFirestore";
import { formatDate, formatCurrency, normalizePhoneNumber } from "@/lib/utils";
import { VehicleType, AppointmentSource } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { getGuestUserId, updateGuestActivity, requestGuestFCMToken } from "@/lib/guestNotifications";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { getAvailableSlotsWithCapacity } from "@/lib/scheduling";
import type { Appointment } from "@shared/schema";
import { trackBeginCheckout, trackPurchase, trackViewPromotion } from "@/lib/analytics";

interface CompletedBooking {
  plate: string;
  customerName: string;
  customerPhone: string;
}

export default function Book() {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();

  const [showWizard, setShowWizard] = useState(true);
  const [reservationCode, setReservationCode] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [completedBookingData, setCompletedBookingData] = useState<CompletedBooking | null>(null);

  const qrCodeRef = useRef<HTMLDivElement>(null);

  const { data: services = [], refetch: refetchServices } = useActiveServices();
  const { data: userVehicles = [] } = useVehicles(user?.id);
  const { data: activeSubscriptions = [] } = useActiveCustomerSubscriptions(user?.id);
  const { data: packages = [] } = useActiveSubscriptionPackages();
  const { data: systemSettings } = useSystemSettings();
  const createVehicle = useCreateVehicle();
  const createAppointment = useCreateAppointment();

  // Get appointments for selected date range (for slot availability)
  const startDate = selectedDate ? formatDate(selectedDate) : formatDate(new Date());
  const endDate = selectedDate ? formatDate(selectedDate) : formatDate(new Date());
  const { data: existingAppointments = [] } = useAppointmentsByDateRange(startDate, endDate);

  // Get available slots for selected date
  const getAvailableSlots = (date?: Date): string[] => {
    if (!date || !systemSettings) return [];
    
    const dateStr = formatDate(date);
    const openTime = systemSettings.operations?.businessHours?.earliestAppointment || "09:00";
    const closeTime = systemSettings.operations?.businessHours?.close || "19:00";
    const duration = systemSettings.operations?.capacity?.slotDurationMinutes || 60;
    const maxCapacity = systemSettings.operations?.capacity?.maxConcurrentCars || 3;
    
    // Convert appointments to scheduling format
    const schedulingAppointments = existingAppointments
      .filter(apt => apt.scheduledDate === dateStr)
      .map(apt => ({
        scheduledDate: apt.scheduledDate,
        scheduledTime: apt.scheduledTime,
        durationMinutes: apt.totalDuration || duration,
      }));
    
    const slots = getAvailableSlotsWithCapacity(
      dateStr,
      duration,
      schedulingAppointments,
      openTime,
      closeTime,
      maxCapacity
    );
    
    return slots.filter(s => s.available).map(slot => slot.time);
  };

  const handleWizardComplete = async (data: BookingFormData & { verificationCode?: string }) => {
    setIsLoading(true);

    try {
      const normalizedPhone = normalizePhoneNumber(data.customerPhone);
      if (!normalizedPhone) {
        throw new Error("Geçersiz telefon numarası");
      }

      // Track booking checkout initiated
      const selectedService = services.find(s => s.id === data.selectedServiceIds[0]);
      const totalPrice = data.selectedServiceIds.reduce((sum, id) => {
        const service = services.find(s => s.id === id);
        return sum + (service?.pricing?.[data.type as keyof typeof service.pricing] || 0);
      }, 0);

      trackBeginCheckout(totalPrice, data.selectedServiceIds.map(id => {
        const service = services.find(s => s.id === id);
        return {
          item_id: id,
          item_name: service?.name || 'Unknown Service',
          item_category: service?.category || 'service',
          price: service?.pricing?.[data.type as keyof typeof service.pricing] || 0,
        };
      }));

      // ✅ VALIDATE SUBSCRIPTION BEFORE BOOKING
      if (data.useSubscription && data.subscriptionId && user) {
        const validateSubscription = httpsCallable(functions, 'validateSubscriptionBooking');
        
        const validationResult: any = await validateSubscription({
          subscriptionId: data.subscriptionId,
          serviceIds: data.selectedServiceIds,
          userId: user.id,
        });
        
        if (!validationResult.data.valid) {
          throw new Error(validationResult.data.message || "Abonelik kullanılamıyor");
        }
        
        console.log("✅ Subscription validated:", validationResult.data);
      }

      // Create reservation (SMS verification already done in wizard if needed)
      await createReservation(data, normalizedPhone, data.verificationCode);
    } catch (error: any) {
      console.error("❌ Booking error:", error);
      console.error("❌ Error code:", error.code);
      console.error("❌ Error message:", error.message);
      console.error("❌ Error details:", error.details);
      console.error("❌ Full error object:", JSON.stringify(error, null, 2));
      
      const errorMessage = error.details || error.message || "Randevu oluşturulamadı";
      
      toast({
        title: "Hata",
        description: errorMessage,
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const createReservation = async (data: BookingFormData, phone: string, verificationCode?: string) => {
    try {
      // Refetch services for registered users to ensure pricing is up-to-date (prevent stale cache issues)
      let currentServices = services;
      if (user) {
        console.log("🔄 Refetching services for registered user to ensure fresh pricing data");
        const result = await refetchServices();
        currentServices = result.data || services; // Use refetched data
        console.log(`✅ Refetched ${currentServices.length} services`);
      }
      
      // Update user phone if needed
      if (user && (!user.phone || normalizePhoneNumber(user.phone) !== phone)) {
        const { updateUser } = await import("@/lib/firestore");
        await updateUser(user.id, { phone, phoneVerified: true });
      }

      const scheduledDate = formatDate(data.selectedDate);
      
      // GUEST USER - Use cloud function
      if (!user) {
        // Validate all services have pricing for selected vehicle type
        const missingPricingServices: string[] = [];
        for (const serviceId of data.selectedServiceIds) {
          const service = currentServices.find(s => s.id === serviceId);
          if (!service) {
            throw new Error(`Hizmet bulunamadı: ${serviceId}`);
          }
          const price = service.pricing?.[data.type as keyof typeof service.pricing];
          if (typeof price !== 'number' || price === 0) {
            missingPricingServices.push(service.name);
          }
        }
        
        if (missingPricingServices.length > 0) {
          throw new Error(`Şu hizmetlerin ${data.type} araç tipi için fiyatı tanımlı değil: ${missingPricingServices.join(', ')}. Lütfen farklı hizmetler seçin veya destek ekibiyle iletişime geçin.`);
        }
        
        // Calculate total duration and amount for guest users (using fresh service data)
        const totalDuration = data.selectedServiceIds.reduce((sum, id) => {
          const service = currentServices.find(s => s.id === id);
          return sum + (service?.durationMinutes || 30);
        }, 0);
        
        const totalAmount = data.selectedServiceIds.reduce((sum, id) => {
          const service = currentServices.find(s => s.id === id);
          if (!service || !data.type) return sum;
          return sum + (service.pricing[data.type as keyof typeof service.pricing] || 0);
        }, 0);
        
        const createGuestBooking = httpsCallable(functions, 'createGuestBooking');

        const finalTotalAmount = data.couponCode 
          ? Math.max(0, totalAmount - (data.discountAmount || 0))
          : totalAmount;

        const result: any = await createGuestBooking({
          customerName: data.customerName,
          customerPhone: phone,
          verificationCode: verificationCode,
          licensePlate: data.plate,
          brand: data.brand,
          model: data.model,
          color: data.color,
          year: null,
          vehicleType: data.type,
          serviceId: data.selectedServiceIds[0],
          serviceIds: data.selectedServiceIds,
          appointmentDate: scheduledDate,
          appointmentTime: data.selectedTime,
          source: AppointmentSource.ONLINE,
          couponCode: data.couponCode || '',
          couponId: data.couponId || null,
          couponType: data.couponType || null,
          couponDiscountPercentage: data.couponDiscountPercentage || null,
          couponFreeServiceId: data.couponFreeServiceId || null,
          discountAmount: data.discountAmount || 0,
          totalAmount: finalTotalAmount,
          totalDuration: totalDuration,
        });

        if (!result.data?.success) {
          throw new Error(result.data?.message || "Randevu oluşturulamadı");
        }

        setReservationCode(result.data.reservationCode);
        await updateGuestActivity(result.data.appointmentId);
        
        // Save completed booking data for QR code
        setCompletedBookingData({
          plate: data.plate,
          customerName: data.customerName,
          customerPhone: phone,
        });
      } else {
        // LOGGED-IN USER - Use Cloud Function for server-side validation
        let vehicleId = data.vehicleId;

        // Create new vehicle if needed
        if (!vehicleId) {
          const vehicle = await createVehicle.mutateAsync({
            userId: user.id,
            plate: data.plate,
            brand: data.brand,
            model: data.model,
            color: data.color,
            type: data.type as typeof VehicleType[keyof typeof VehicleType],
          });
          vehicleId = vehicle.id;
        }

        // Calculate total duration and amount for telemetry (Cloud Function will validate)
        const totalDuration = data.selectedServiceIds.reduce((sum, id) => {
          const service = currentServices.find(s => s.id === id);
          return sum + (service?.durationMinutes || 30);
        }, 0);
        
        const baseAmount = data.useSubscription ? 0 : data.selectedServiceIds.reduce((sum, id) => {
          const service = currentServices.find(s => s.id === id);
          if (!service || !data.type) return sum;
          return sum + (service.pricing[data.type as keyof typeof service.pricing] || 0);
        }, 0);
        
        const totalAmount = data.couponCode && !data.useSubscription
          ? Math.max(0, baseAmount - (data.discountAmount || 0))
          : baseAmount;

        // Call Cloud Function with server-side validation
        console.log("📞 Calling createAuthenticatedAppointment...");
        const createAuthAppointment = httpsCallable(functions, 'createAuthenticatedAppointment');

        const callData = {
          vehicleId: vehicleId,
          serviceIds: data.selectedServiceIds,
          scheduledDate: scheduledDate,
          scheduledTime: data.selectedTime,
          customerPhone: phone,
          source: AppointmentSource.ONLINE,
          notes: '',
          totalDuration: totalDuration,
          totalAmount: totalAmount,
          useSubscription: data.useSubscription || false,
          subscriptionId: data.subscriptionId || null,
          couponCode: data.couponCode || null,
          couponId: data.couponId || null,
          couponType: data.couponType || null,
          couponDiscountPercentage: data.couponDiscountPercentage || null,
          couponFreeServiceId: data.couponFreeServiceId || null,
          discountAmount: data.discountAmount || 0,
        };
        
        console.log("📝 Call data:", JSON.stringify(callData, null, 2));
        
        const result = await createAuthAppointment(callData);
        console.log("✅ Result:", result);

        const responseData = result.data as { success: boolean; reservationCode: string; appointmentId: string; message?: string };
        
        if (!responseData.success) {
          throw new Error(responseData.message || "Randevu oluşturulamadı");
        }

        setReservationCode(responseData.reservationCode);
        
        // Save completed booking data for QR code
        setCompletedBookingData({
          plate: data.plate,
          customerName: data.customerName,
          customerPhone: phone,
        });
      }

      // Track successful booking (purchase event)
      const basePrice = data.selectedServiceIds.reduce((sum, id) => {
        const service = services.find(s => s.id === id);
        return sum + (service?.pricing?.[data.type as keyof typeof service.pricing] || 0);
      }, 0);
      
      const finalPrice = data.useSubscription 
        ? 0 
        : data.couponCode 
          ? Math.max(0, basePrice - (data.discountAmount || 0))
          : basePrice;

      trackPurchase({
        transaction_id: reservationCode || 'TEMP',
        value: finalPrice,
        currency: 'TRY',
        items: data.selectedServiceIds.map(id => {
          const service = services.find(s => s.id === id);
          return {
            item_id: id,
            item_name: service?.name || 'Unknown Service',
            item_category: service?.category || 'service',
            price: service?.pricing?.[data.type as keyof typeof service.pricing] || 0,
            quantity: 1,
          };
        }),
        customer_type: user ? 'registered' : 'guest',
        payment_method: data.useSubscription ? 'subscription' : 'cash',
      });

      setShowWizard(false);
      setShowConfirmation(true);
      setIsLoading(false);

      toast({
        title: "Randevu Oluşturuldu! 🎉",
        description: "Rezervasyon kodunuz SMS ile gönderildi.",
      });

      // Request FCM token for guest users (non-blocking, progressive notification permission)
      if (!user) {
        try {
          const token = await requestGuestFCMToken();
          if (token) {
            console.log("✅ Guest FCM token registered after booking");
          }
        } catch (error) {
          // FCM is optional - don't interrupt the booking flow
          console.log("ℹ️ Guest FCM token registration skipped (permission denied or not supported)");
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  const downloadQRCode = async () => {
    try {
      const qrUrl = `${window.location.origin}/track?code=${reservationCode}&plate=${completedBookingData?.plate}`;
      const canvas = await QRCodeCanvas.toCanvas(qrUrl, {
        errorCorrectionLevel: 'H',
        width: 512,
        margin: 2,
      });

      const link = document.createElement('a');
      link.download = `temizfilo-${reservationCode}.png`;
      link.href = canvas.toDataURL();
      link.click();

      toast({
        title: "QR Kod İndirildi",
        description: "QR kodunuz başarıyla indirildi.",
      });
    } catch (error) {
      toast({
        title: "Hata",
        description: "QR kod indirilemedi.",
        variant: "destructive",
      });
    }
  };

  if (showConfirmation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
        <PopupDisplay pageId="book" />
        
        <Card className="max-w-2xl w-full p-8 sm:p-12 text-center">
          <div className="mb-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">Randevunuz Alındı!</h1>
            <p className="text-lg text-muted-foreground">
              Rezervasyon kodunuz SMS ile gönderildi
            </p>
          </div>

          <div className="bg-primary/5 p-8 rounded-2xl mb-6">
            <div className="text-sm text-muted-foreground mb-2">Rezervasyon Kodunuz</div>
            <div className="text-5xl font-bold text-primary tracking-wider">
              {reservationCode}
            </div>
          </div>

          <div className="bg-background p-6 rounded-xl mb-6" ref={qrCodeRef}>
            <QRCode
              value={`${window.location.origin}/track?code=${reservationCode}&plate=${completedBookingData?.plate}`}
              size={200}
              className="mx-auto"
            />
            <p className="text-xs text-muted-foreground mt-4">
              QR kodunu tarayarak randevunuzu takip edebilirsiniz
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={downloadQRCode}
              className="flex-1"
            >
              <Download className="w-4 h-4 mr-2" />
              QR Kodu İndir
            </Button>
            <Button
              onClick={() => navigate(`/track?code=${reservationCode}&plate=${completedBookingData?.plate}`)}
              className="flex-1"
            >
              Randevuyu Takip Et
            </Button>
          </div>

          <div className="mt-8 pt-8 border-t">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="text-muted-foreground"
            >
              Ana Sayfaya Dön
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (showWizard) {
    const slots = getAvailableSlots(selectedDate);

    return (
      <div>
        <PopupDisplay pageId="book" />
        <BookingWizard
          isGuest={!user}
          customerId={user?.id || null}
          phoneVerified={user?.phoneVerified || false}
          existingVehicles={userVehicles.map(v => ({
            id: v.id,
            plate: v.plate,
            brand: v.brand,
            model: v.model,
            color: v.color,
            type: v.type as 'sedan' | 'suv' | 'large',
          }))}
          activeSubscriptions={activeSubscriptions.map(sub => ({
            id: sub.id,
            packageName: packages.find(p => p.id === sub.packageId)?.name || 'Bilinmeyen Paket',
            remainingExteriorWashes: sub.remainingExteriorWashes,
            remainingInteriorCleans: sub.remainingInteriorCleans,
            remainingDetailings: sub.remainingDetailings,
            remainingExtraServices: 0,
            expiresAt: new Date(sub.endDate).toISOString(),
          }))}
          services={services.map(s => ({
            id: s.id,
            name: s.name,
            active: s.active,
            category: s.category,
            isPopular: s.isPopular,
            pricing: s.pricing,
          }))}
          getAvailableSlots={getAvailableSlots}
          onDateChange={(date) => setSelectedDate(date)}
          onComplete={handleWizardComplete}
          onCancel={() => navigate("/")}
          defaultName={user?.displayName || ""}
          defaultPhone={user?.phone || ""}
        />
      </div>
    );
  }

  return null;
}
